import React, { useState, useEffect, useRef } from 'react';
import YouTube from 'react-youtube';
import { useNavigate } from 'react-router-dom'; // 引入 useNavigate hook
import {
  EuiPanel,
  EuiIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiButton,
  EuiSelect,
  EuiButtonIcon,
  EuiListGroup,
  EuiListGroupItem,
  EuiModal,
  EuiModalHeader,
  EuiModalBody,
  EuiModalFooter,
  EuiFieldText,
  EuiTextArea,
  EuiDragDropContext,
  EuiDroppable,
  EuiDraggable,
  EuiButtonEmpty,
  EuiText,
  EuiHeader,
  EuiHeaderSection,
  EuiHeaderSectionItem,
  copyToClipboard,
  euiDragDropReorder,
} from '@elastic/eui';
import { TranslateLanguage } from './KeywordTranslator';
import '../style.css';
import logosmall from '../assets/CTBLogoCircle-Small.png';
import axios from 'axios';
import TopRightMenu from "../components/TopRightMenu";
import LanguageToggle from "../components/LanguageToggle";
import { useAlert } from './AlertProvider';
import { keepScreenAwakeForPlayback, releaseScreenAwakeForPlayback } from './nativePlaybackBridge';

const apiURL = process.env.REACT_APP_MasterApiURL;
const apiKey = process.env.REACT_APP_APIKey;

const opts = {
  height: '100%',
  width: '100%',
  playerVars: {
    autoplay: 1,
    playsinline: 1,
    enablejsapi: 1,
    fullscreen: 1,
  },
};

const onPlayerError = (event) => {
  console.error('Error occurred while playing video:', event.data);
};

const onReady = (e) => {
  e.target.playVideo();
};

function FavoritePageWithPlayer() {
  const [playlists, setPlaylists] = useState([]);
  const [playlistItemCounts, setPlaylistItemCounts] = useState({});
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [songs, setSongs] = useState([]);
  const [currentSong, setCurrentSong] = useState({ id: '', name: '' });
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [currentResource, setCurrentResource] = useState('');
  const [newTags, setNewTags] = useState('');
  const [isPanelVisible, setIsPanelVisible] = useState(true); // 控制右侧面板是否可见
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlaylistVisibility, setNewPlaylistVisibility] = useState('private');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [newPlaylistTags, setNewPlaylistTags] = useState('');
  const [newPlaylistCategory, setNewPlaylistCategory] = useState('');
  const [expandedPlaylist, setExpandedPlaylist] = useState(null); // 用于记录当前展开的收藏单
  const [playlistDetails, setPlaylistDetails] = useState(null); // 用于存储收藏单详情数据
  const [shareLink, setShareLink] = useState("");
  const navigate = useNavigate(); // 使用 useNavigate 创建 navigate 函数
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  const showAlert = useAlert();
  const [verses, setVerses] = useState(''); // 使用 useState 定义 verses
  const currentSongRef = useRef(currentSong); // 使用 useRef 存储最新的 currentSong
  const songsRef = useRef(songs);
  const [isLoadingPlaylists, setIsLoadingPlaylists] = useState(true);
  const [isLoadingSongs, setIsLoadingSongs] = useState(true);

  // ✅ 收藏单排序模式
  const [isSortMode, setIsSortMode] = useState(false);
  const [isSavingPlaylistOrder, setIsSavingPlaylistOrder] = useState(false);
  const playlistsSnapshotRef = useRef([]);

  // 从 sessionStorage 获取 userId
  const user = JSON.parse(sessionStorage.getItem('user'));
  const userId = user ? user.id : null;

  // 判斷是否有收藏單/收藏內容
  const hasPlaylists = Array.isArray(playlists) && playlists.length > 0;
  const hasSongs = Array.isArray(songs) && songs.length > 0;
  const showEmpty = !isLoadingPlaylists && !isLoadingSongs && (!hasPlaylists || !hasSongs);

  const syncPlaylistItemCount = (playlistId, count) => {
    const normalizedCount = Number.isFinite(Number(count)) ? Number(count) : 0;
    setPlaylistItemCounts(previousCounts => {
      if (previousCounts[playlistId] === normalizedCount) return previousCounts;
      return { ...previousCounts, [playlistId]: normalizedCount };
    });
  };

  useEffect(() => {
    setIsLoadingPlaylists(true);
    axios.get(`/api/playlists`, {
      params: {
        userId: userId,
        type: 'favorite',
      },
    })
      .then(response => {
        const data = response.data || [];
        // ✅ 默认按 pinned/orderIndex 排好（后端也会排序，这里做兜底）
        const sorted = [...data].sort((a, b) => {
          const ap = a.pinned ? 1 : 0;
          const bp = b.pinned ? 1 : 0;
          if (bp !== ap) return bp - ap;
          const ao = a.orderIndex ?? 0;
          const bo = b.orderIndex ?? 0;
          if (ao !== bo) return ao - bo;
          return (a.id ?? 0) - (b.id ?? 0);
        });
        setPlaylists(sorted);
        setPlaylistItemCounts(Object.fromEntries(
          sorted.map(playlist => [
            playlist.id,
            Number.isFinite(Number(playlist.songCount)) ? Number(playlist.songCount) : 0
          ])
        ));
        setIsLoadingPlaylists(false);
      })
      .catch(error => {
        console.error('Error fetching playlists:', error);
      });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // const panelStyle = {
  //   position: 'sticky',
  //   top: 0,
  //   zIndex: 100,
  //   height: isPanelVisible
  //     ? (isPortrait ? '50vh' : '90vh') // When panel is visible
  //     : (isPortrait ? '90vh' : '90vh'), // When panel is not visible
  // };

  const panelStyle = {
    position: 'relative',
    width: '100%',
    height: 'auto',
    minHeight: 0,
    overflow: 'visible'
  };

  // 同步更新 currentSongRef
  useEffect(() => {
    currentSongRef.current = currentSong;
  }, [currentSong]);

  useEffect(() => {
    songsRef.current = songs;
  }, [songs]);

  const handlePlaylistChange = (playlistId) => {
    if (isSortMode) return; // ✅ 排序模式下不允许展开/切换

    if (expandedPlaylist === playlistId) {
      setExpandedPlaylist(null); // 如果点击的收藏单已经展开，则合上
      return;
    }

    // 再次展開目前收藏單時保留當前內容與播放進度。
    if (selectedPlaylist === playlistId) {
      setExpandedPlaylist(playlistId);
      return;
    }

    setSelectedPlaylist(playlistId);
    setIsLoadingSongs(true);
    axios.get(`/api/playlists/${playlistId}/resources`)
      .then(response => {
        if (response.status !== 200) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const sortedReses = (response.data || []).sort((a, b) => a.order - b.order); // 按 order 排序
        setSongs(sortedReses);
        syncPlaylistItemCount(playlistId, sortedReses.length);
        setExpandedPlaylist(playlistId); // 展开新的收藏单
        setIsLoadingSongs(false);
      })
      .catch(error => {
        console.error('Error fetching resources:', error);
        setIsLoadingSongs(false);
      });
  };

  // =========================
  // ✅ 收藏单排序模式：置顶/上移/下移/保存
  // =========================
  const normalizePlaylistsOrderLocal = (list) => {
    const pinned = list.filter(p => !!p.pinned);
    const normal = list.filter(p => !p.pinned);
    pinned.forEach((p, i) => { p.orderIndex = i; });
    normal.forEach((p, i) => { p.orderIndex = i; });
    return [...pinned, ...normal];
  };

  const handleEnterSortMode = () => {
    playlistsSnapshotRef.current = JSON.parse(JSON.stringify(playlists || []));
    setExpandedPlaylist(null);
    setIsSortMode(true);
  };

  const handleCancelSortMode = () => {
    setPlaylists(playlistsSnapshotRef.current || []);
    setIsSortMode(false);
  };

  const handleTogglePlaylistPinned = (playlistId) => {
    setPlaylists(prev => {
      const list = JSON.parse(JSON.stringify(prev || []));
      const target = list.find(p => p.id === playlistId);
      if (!target) return prev;
      target.pinned = !target.pinned;
      return normalizePlaylistsOrderLocal(list);
    });
  };

  const handleMovePlaylist = (playlistId, direction) => {
  // direction: -1 上移, +1 下移（仅在同组内移动）
  setPlaylists(prev => {
    const list = JSON.parse(JSON.stringify(prev || []));

    // 先按 pinned + orderIndex 排序成“当前展示顺序”，否则移动会不生效
    const pinned = list.filter(p => !!p.pinned).sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    const normal = list.filter(p => !p.pinned).sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

    const inPinned = pinned.some(p => p.id === playlistId);
    const group = inPinned ? pinned : normal;

    const pos = group.findIndex(p => p.id === playlistId);
    if (pos === -1) return prev;

    const newPos = pos + direction;
    if (newPos < 0 || newPos >= group.length) return prev;

    // 交换数组位置（而不是仅交换 orderIndex；否则 normalize 会把 orderIndex 重置掉）
    const tmp = group[pos];
    group[pos] = group[newPos];
    group[newPos] = tmp;

    // 重新归一化（组内从 0..N-1）
    pinned.forEach((p, i) => { p.orderIndex = i; });
    normal.forEach((p, i) => { p.orderIndex = i; });

    return [...pinned, ...normal];
  });
};

  const handleSavePlaylistOrder = async () => {
    try {
      setIsSavingPlaylistOrder(true);

      const list = normalizePlaylistsOrderLocal(JSON.parse(JSON.stringify(playlists || [])));

      const items = list.map(p => ({
        id: p.id,
        pinned: !!p.pinned,
        order: p.orderIndex ?? 0,
      }));

      await axios.put(`/api/playlists/order`, { items }, {
        headers: { 'Content-Type': 'application/json' },
      });

      setPlaylists(list);
      setIsSortMode(false);
      showAlert(TranslateLanguage(localStorage.userLanguage, "收藏單排序已保存"));
    } catch (e) {
      console.error('Error saving playlist order:', e);
      showAlert(TranslateLanguage(localStorage.userLanguage, "收藏單保存失敗，請稍後重試"));
    } finally {
      setIsSavingPlaylistOrder(false);
    }
  };

  //删除收藏項目的处理函数
  const handleDeleteSong = (songId) => {
    if (window.confirm(TranslateLanguage(localStorage.userLanguage, "確定從收藏單中刪除？"))) {
      axios.delete(`/api/playlists/${selectedPlaylist}/songs/${songId.resourceId}`)
        .then(response => {
          if (response.status === 200) {
            // 删除成功后更新前端的收藏项列表
            const updatedSongs = songs.filter(song => song.resourceId !== songId.resourceId);
            setSongs(updatedSongs);
            syncPlaylistItemCount(selectedPlaylist, updatedSongs.length);
            showAlert(TranslateLanguage(localStorage.userLanguage, "刪除成功"));
          } else {
            showAlert(TranslateLanguage(localStorage.userLanguage, "刪除失敗，請重試"));
          }
        })
        .catch(error => {
          console.error("删除收藏項目时出错:", error);
          showAlert(TranslateLanguage(localStorage.userLanguage, "刪除失敗，請重試"));
        });
    }
  };

  // 当 playlists 加载完成后，自动选择第一个收藏单并触发其点击事件
  useEffect(() => {
    if (playlists.length > 0) {
      handlePlaylistChange(playlists[0].id); // 自动选择第一个收藏单
    } else {
      setSelectedPlaylist(null);
    }
  }, [playlists]);

  // 当收藏項目列表(songs)更新时，自动播放第一个收藏单的第一個收藏項目
  useEffect(() => {
    if (songs.length > 0) {
      setCurrentSong(songs[0]);
    }
  }, [songs]);

  const handleSongClick = (song) => {
    setCurrentSong(song);
  };

  // const handlePreviousSong = () => {
  //   if (currentSongRef.current) {
  //     let currentIndex = songs.findIndex((s) => s.resourceId === currentSongRef.current.resourceId);
  //     let previousIndex;
  //     previousIndex = (currentIndex - 1 + songs.length) % songs.length;
  //     setCurrentSong(songs[previousIndex]);
  //   }
  // };
  const handlePreviousSong = () => {
    if (!songs.length || !currentSongRef.current) {
      return;
    }

    const currentIndex = songs.findIndex(
      song =>
        song.resourceId ===
        currentSongRef.current.resourceId
    );

    const safeIndex =
      currentIndex >= 0 ? currentIndex : 0;

    const previousIndex =
      (safeIndex - 1 + songs.length) %
      songs.length;

    setCurrentSong(songs[previousIndex]);
  };

  // const handleNextSong = () => {
  //   if (currentSongRef.current) {
  //     let currentIndex = songsRef.current.findIndex((s) => s.resourceId === currentSongRef.current.resourceId);
  //     let nextIndex;
  //     nextIndex = (currentIndex + 1) % songsRef.current.length;
  //     setCurrentSong(songsRef.current[nextIndex]);
  //   }
  // };
  const handleNextSong = () => {
    const currentSongs = songsRef.current;

    if (
      !currentSongs.length ||
      !currentSongRef.current
    ) {
      return;
    }

    const currentIndex = currentSongs.findIndex(
      song =>
        song.resourceId ===
        currentSongRef.current.resourceId
    );

    const safeIndex =
      currentIndex >= 0 ? currentIndex : 0;

    const nextIndex =
      (safeIndex + 1) %
      currentSongs.length;

    setCurrentSong(currentSongs[nextIndex]);
  };

  const getTextAlignment = (resource) => {
    if (resource && resource.resourceType === '經文') {
      return 'right'; // 如果是經文，文本靠右
    }
    return 'left'; // 其他情况文本靠左
  };

  // function setViewportDimensions() {
  //   let height = 44;
  //   let calwidth;
  //   let calheight;
  //   calwidth = window.innerWidth - 30;
  //   calheight = window.innerHeight - height;
  //   if (calwidth * 9 / 16 > calheight)
  //     document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight - height}px`);
  //   else
  //     document.documentElement.style.setProperty('--viewport-height', `${calwidth * 9 / 16}px`);
  //   document.documentElement.style.setProperty('--viewport-width', `${window.innerWidth - 20}px`);
  // }

  // useEffect(() => {
  //   setViewportDimensions();
  //   const handler = () => setViewportDimensions();
  //   window.addEventListener('resize', handler);
  //   return () => window.removeEventListener('resize', handler);
  // }, []);

  useEffect(() => {
  const updateViewportDimensions = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const reservedHeight = 44;

    const availableWidth = Math.max(0, width - 30);
    const availableHeight = Math.max(
      0,
      height - reservedHeight
    );

    const playerHeight = Math.min(
      availableWidth * 9 / 16,
      availableHeight
    );

    document.documentElement.style.setProperty(
      '--viewport-height',
      `${playerHeight}px`
    );

    document.documentElement.style.setProperty(
      '--viewport-width',
      `${Math.max(0, width - 20)}px`
    );

    // 不要用键盘改变后的可见高度判断横竖屏
    setIsPortrait(
      window.matchMedia('(orientation: portrait)').matches
    );
  };

  updateViewportDimensions();

  window.addEventListener(
    'resize',
    updateViewportDimensions
  );

  return () => {
    window.removeEventListener(
      'resize',
      updateViewportDimensions
    );
  };
}, []);

  function extractContent(stringHTML) {
    var content = ""
    var span = document.createElement('span');
    span.innerHTML = stringHTML;
    var contextExtracted = span.getElementsByTagName("table")[0];
    if (contextExtracted) {
      contextExtracted.getElementsByTagName("u")[0].remove();
      contextExtracted.querySelectorAll("span, p, strong")
        .forEach(el => el.textContent.trim() === "" && el.parentNode.removeChild(el));
      content = contextExtracted.innerHTML;
    }

    return content;
  };

  const textTitleStyle = {
    fontSize: '20px',
    lineHeight: '1.6',
    color: '#333',
    padding: '15px',
    whiteSpace: 'pre-wrap',
    overflowY: 'auto',
    textAlign: 'center',
    fontWeight: '600',
  };

  // const textContainerStyle = {
  //   height: isPanelVisible
  //     ? (isPortrait ? 'calc(50vh - 52px)' : 'calc(90vh - 52px)')
  //     : (isPortrait ? 'calc(90vh - 52px)' : 'calc(90vh - 52px)'),
  //   overflow: 'auto',
  //   padding: '10px',
  //   border: '1px solid #ccc',
  //   borderRadius: '5px',
  //   backgroundColor: '#f9f9f9',
  // };
  const textContainerStyle = {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    height: 'auto',
    minHeight: 0,
    overflow: 'visible',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '5px',
    backgroundColor: '#f9f9f9'
  };
  // const textContainerStyleWithTitle = {
  //   height: isPanelVisible
  //     ? (isPortrait ? 'calc(50vh - 100px)' : 'calc(90vh - 100px)')
  //     : (isPortrait ? 'calc(90vh - 100px)' : 'calc(90vh - 100px)'),
  //   overflow: 'auto',
  //   padding: '10px',
  //   border: '1px solid #ccc',
  //   borderRadius: '5px',
  //   backgroundColor: '#f9f9f9',
  // };
  const textContainerStyleWithTitle = {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    height: 'auto',
    minHeight: 0,
    overflow: 'visible',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '5px',
    backgroundColor: '#f9f9f9'
  };
  const textVerseContainerStyle = {
    // height: isPanelVisible
    //   ? (isPortrait ? 'calc(50vh - 52px)' : 'calc(90vh - 52px)')
    //   : (isPortrait ? 'calc(90vh - 52px)' : 'calc(90vh - 52px)'),
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    height: 'auto',
    minHeight: 0,
    overflow: 'visible',
    overflowWrap: 'anywhere',
    border: '1px solid #ccc',
    borderRadius: '5px',
    fontSize: '20px', // 字体大小
    lineHeight: '1.8', // 行高
    padding: '20px', // 内边距
    margin: '10px 0', // 只保留垂直间距，避免超出父容器
    color: '#333', // 字体颜色，深灰色
    backgroundColor: '#f7f7f7', // 背景颜色，淡灰色
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)', // 阴影效果，使容器更加立体
    // overflowY: 'auto', // 支持滚动
    // maxHeight: '70vh', // 限制容器高度，避免页面过长
    whiteSpace: 'pre-wrap', // 保持空白和换行
    fontWeight: '520', // 设置文字粗细为中等粗
  };

  useEffect(() => {
    if (!currentSong.engineID) return;
    const [enginePrefix, engineNumberStr] = currentSong.engineID.split("|");
    const engineNumber = parseInt(engineNumberStr, 10);
    setCurrentResource(null);
    fetch(apiURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json, charset=UTF-8",
        "Authorization": "Bearer search-" + apiKey
      },
      body: JSON.stringify({
        query: "",
        filters: { "any": { "id": currentSong.engineID } },
        page: { size: 1000 },
        sort: { id: "asc" }
      })
    })
      .then(response => response.json())
      .then(data => {
        const results = data.results;
        if (results.length > 0) {
          const result = results[0];
          if (result.doctype.raw === "禱告") {
            setCurrentResource(result.prayer.raw);
          } else if (result.doctype.raw === "經文") {
            const verseNumber = parseInt(result.verse_number.raw, 10);
            let startVerse, endVerse;
            // 解析 description 字段，获取起始和结束节
            const descriptionRange = currentSong.description.split(",");
            if (
              descriptionRange.length === 2 &&
              descriptionRange[0] !== descriptionRange[1]
            ) {
              startVerse = parseInt(descriptionRange[0], 10);
              endVerse = parseInt(descriptionRange[1], 10);
            } else {
              startVerse = verseNumber;
              endVerse = verseNumber;
            }
            // 计算新的 engineID 范围
            const startEngineID = `${enginePrefix}|${engineNumber + startVerse - verseNumber}`;
            const endEngineID = `${enginePrefix}|${engineNumber + endVerse - verseNumber}`;
            if (startEngineID !== endEngineID) {
              // 生成需要的 ID 列表
              const idList = [];
              for (let i = startVerse; i <= endVerse; i++) {
                idList.push(`${enginePrefix}|${engineNumber + i - verseNumber}`);
              }
              // 发起新的查询以获取多节经文
              fetch(apiURL, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json, charset=UTF-8",
                  "Authorization": "Bearer search-" + apiKey
                },
                body: JSON.stringify({
                  query: "",
                  filters: {
                    "any": {
                      "id": idList // 使用批量ID查询
                    }
                  },
                  page: { size: 1000 },
                  sort: { id: "asc" }
                })
              })
                .then(response => response.json())
                .then(data => {
                  const newVerses = data.results
                    .map(v => `${v.verse_number.raw} ${v.title.raw}`) // 在每段前面添加節數
                    .join(" ");
                  setVerses(newVerses);
                  setCurrentResource(`${result.chapter_name.raw} ${result.chapter_number.raw}: ${startVerse}-${endVerse}`);
                })
                .catch(() => {
                  setCurrentResource(null);
                });
            }
            else {
              setVerses(currentSong.name);
              setCurrentResource(`${result.chapter_name.raw} ${result.chapter_verse_number.raw}`);
            }
          } else if (["拾穗", "註解", "例證", "綱目"].includes(result.doctype.raw)) {
            let resultText = formatText(result.title.raw);
            const resultLink = result.link.raw;
            loadContentLocal(resultLink, resultText);
          } else if (result.doctype.raw === "網路") {
            setCurrentResource(result.description.raw);
          } else {
            setCurrentResource(null);
          }
        } else {
          setCurrentResource(null);
        }
      })
      .catch(() => {
        setCurrentResource(null);
      });
  }, [currentSong, apiURL, apiKey]);

  const loadContentLocal = async (filePath, resultText) => {
    filePath = filePath.replace("https://www.ccbiblestudy.org/New Testament", "")
      .replace("https://www.ccbiblestudy.org/Old Testament", "");
    const finalFilePath = `${window.location.origin}/ccbiblestudy${filePath}`;

    const response = await fetch(finalFilePath);
    const data = await response.text();
    const finalResult = extractContent(data);
    setCurrentResource(finalResult || resultText);
  };

  const formatText = (text) => {
    return text.split("壹、內容綱要").join("<p>壹、內容綱要</p>")
      .split("貳、逐節詳解").join("<p>貳、逐節詳解</p>")
      .split("叁、靈訓要義").join("<p>叁、靈訓要義</p>")
      .split("〔呂振中譯〕").join("<br>〔呂振中譯〕<br>")
      .split("〔原文字義〕").join("<br>〔原文字義〕<br>")
      .split("〔文意註解〕").join("<br>〔文意註解〕<br>")
      .split("〔話中之光〕").join("<br>〔話中之光〕<br>")
      .split("【").join("<p>【")
      .split("】").join("】</p>")
      + "<p>。。。（前往原始網站查看更多）";
  };

  const handleEditClick = () => {
    if (selectedPlaylist) {
      axios.get(`/api/playlists/${selectedPlaylist}`)
        .then(response => {
          if (response.status !== 200) {
            throw new Error('Network response was not ok');
          }
          const data = response.data;
          setPlaylistDetails(data);
          setNewPlaylistName(data.name);
          setNewPlaylistVisibility(data.visibility);
          setNewPlaylistDescription(data.description);
          setNewPlaylistTags(data.tags);
          setNewPlaylistCategory(data.category);
          setIsEditModalVisible(true);
        })
        .catch(error => {
          console.error('Error fetching playlist details:', error);
        });
    }
  };

  // 处理播放结束事件，自动播放下一個收藏
  const handleSongEnd = () => {
    const currentIndex = songs.findIndex(song => song.resourceId === currentSong.resourceId);
    const nextIndex = (currentIndex + 1) % songs.length;
    if (nextIndex === 0) {
      setCurrentSong(songs[0]); // 回到第一個收藏
    } else {
      setCurrentSong(songs[nextIndex]);
    }
  };

  //保存修改後的收藏單信息
  const handleSaveChanges = () => {
    const requestBody = {
      name: newPlaylistName,
      visibility: newPlaylistVisibility,
      description: newPlaylistDescription,
      tags: newPlaylistTags,
      category: newPlaylistCategory
    };

    axios.put(`/api/playlists/${selectedPlaylist}`, requestBody, {
      headers: {
        'Content-Type': 'application/json',
      }
    })
      .then(() => {
        setIsEditModalVisible(false);
        const updatedPlaylists = playlists.map(p => p.id === selectedPlaylist ? { ...p, ...requestBody } : p);
        setPlaylists(updatedPlaylists);
      })
      .catch(error => {
        console.error('Error updating playlist:', error);
      });
  };

  // 打开新建收藏单模态窗口
  const openCreatePlaylistModal = () => {
    setNewPlaylistName("");
    setNewPlaylistDescription("");
    setNewPlaylistTags("");
    setNewPlaylistCategory("");
    setShowCreateModal(true);
  };

  // 关闭新建收藏单模态窗口
  const closeCreatePlaylistModal = () => {
    setShowCreateModal(false);
  };

  // 创建新收藏单
  const handleCreatePlaylist = () => {
    const requestBody = {
      userId: user.id,
      name: newPlaylistName,
      type: 'favorite',
      visibility: newPlaylistVisibility,
      description: newPlaylistDescription,
      tags: newPlaylistTags,
      category: newPlaylistCategory
    };

    axios.post(`/api/playlists/create`, requestBody, {
      headers: {
        'Content-Type': 'application/json',
      }
    })
      .then(response => {
        const data = response.data;
        showAlert(TranslateLanguage(localStorage.userLanguage, "收藏單創建成功"));
        closeCreatePlaylistModal();

        // 新建后附加到列表末尾（后端会给 orderIndex；这里先局部追加）
        setPlaylists([...playlists, { id: data.playlistId, name: newPlaylistName, pinned: false, orderIndex: playlists.length, songCount: 0 }]);
        syncPlaylistItemCount(data.playlistId, 0);
      })
      .catch(error => {
        if (error.response) {
          console.error('响应错误:', error.response.data);
          console.error('状态码:', error.response.status);
        } else if (error.request) {
          console.error('请求未响应:', error.request);
        } else {
          console.error('请求配置错误:', error.message);
        }
      });
  };

  //生成分享鏈接
  const handleSharePlaylistClick = () => {
    if (songs.length == 0) {
      showAlert(TranslateLanguage(localStorage.userLanguage, "收藏單中沒有内容，請添加后再分享。"));
    }
    else {
      const urlPrefix = window.location.origin;
      axios.get(`/api/playlists/share/${selectedPlaylist}`)
        .then(response => {
          const data = response.data;
          if (data.shareLink) {
            const shareLink = `${urlPrefix}${data.shareLink}`;
            setShareLink(shareLink);
          } else {
            showAlert(TranslateLanguage(localStorage.userLanguage, "無法生成分享鏈接，可能收藏單是私有的。"));
          }
        })
        .catch(error => {
          console.error("生成分享链接时出错:", error);
          showAlert(TranslateLanguage(localStorage.userLanguage, "生成分享鏈接失敗，請點擊“詳情”設置為“公開”！"));
        });
    }
  };

  //刪除收藏單
  const handleDeletePlaylistClick = () => {
    if (window.confirm(TranslateLanguage(localStorage.userLanguage, "確定刪除這個收藏單？"))) {
      axios.delete(`/api/playlists/${selectedPlaylist}`)
        .then(response => {
          if (response.status === 200) {
            setPlaylists(playlists.filter(playlist => playlist.id !== selectedPlaylist));
            setPlaylistItemCounts(previousCounts => {
              const nextCounts = { ...previousCounts };
              delete nextCounts[selectedPlaylist];
              return nextCounts;
            });
            showAlert(TranslateLanguage(localStorage.userLanguage, "收藏單刪除成功"));
          } else {
            showAlert(TranslateLanguage(localStorage.userLanguage, "刪除失敗，請重試"));
          }
        })
        .catch(error => {
          console.error("删除收藏單时出错:", error);
          showAlert(TranslateLanguage(localStorage.userLanguage, "刪除失敗，請重試"));
        });
    }
  };

  const copyShareLink = () => {
    copyToClipboard(shareLink);
    setShareLink(false);
  };

  const onDragEnd = ({ source, destination }) => {
    if (source && destination) {
      const reorderedSongs = euiDragDropReorder(songs, source.index, destination.index);
      setSongs(reorderedSongs);
      const songOrders = reorderedSongs.map((song, index) => ({
        songId: song.resourceId,
        order: index + 1
      }));

      const payload = {
        playlistId: selectedPlaylist,
        songOrders: songOrders
      };
      axios.post(`/api/playlists/${selectedPlaylist}/reorder`, payload, {
        headers: {
          'Content-Type': 'application/json',
        }
      })
        .then(response => {
          if (response.status !== 200) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          setSongs([...reorderedSongs]);
        })
        .catch(error => {
          console.error('Error saving song order:', error);
        });
    }
  };

  const togglePanel = () => {
    setIsPanelVisible(!isPanelVisible);
  };

  const renderPlaylistRow = (playlist) => {
    const itemCount = playlistItemCounts[playlist.id] ?? playlist.songCount ?? 0;
    const pinned = !!playlist.pinned;
    const group = playlists.filter(p => (!!p.pinned) === pinned);
    const groupSorted = [...group].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    const indexInGroup = groupSorted.findIndex(p => p.id === playlist.id);

    return (
      <div key={playlist.id}>
        <EuiFlexGroup
          alignItems="center"
          gutterSize="s"
          direction="row"
          wrap={false}
          responsive={false}
        >
          <EuiFlexItem grow={1} style={{ minWidth: 0, marginRight: 8 }}>
            <EuiListGroupItem
              className={`playlist-item ${selectedPlaylist === playlist.id ? 'selected-playlist' : ''}`}
              label={
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px', maxWidth: '100%' }}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {TranslateLanguage(localStorage.userLanguage, playlist.name)}
                  </span>
                  <span style={{ color: '#69707d', fontSize: '0.72em', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {itemCount}項
                  </span>
                </span>
              }
              onClick={() => handlePlaylistChange(playlist.id)}
              iconType={expandedPlaylist === playlist.id ? 'arrowDown' : 'arrowRight'}
              isDisabled={isSortMode}
              style={{ width: "100%", overflow: "hidden", pointerEvents: isSortMode ? "none" : "auto" }}
            />
          </EuiFlexItem>

          {isSortMode && (
            <EuiFlexItem grow={false} style={{ flexShrink: 0 }}>
              <EuiFlexGroup gutterSize="xs" responsive={false} alignItems="center" wrap={false}>
                <EuiFlexItem grow={false}>
                  <EuiButtonIcon
                    iconType={playlist.pinned ? 'pinFilled' : 'pin'}
                    aria-label={playlist.pinned ? '取消置顶' : '置顶'}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleTogglePlaylistPinned(playlist.id); }}
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButtonIcon
                    iconType="sortUp"
                    aria-label="上移"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMovePlaylist(playlist.id, -1); }}
                    isDisabled={indexInGroup <= 0}
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButtonIcon
                    iconType="sortDown"
                    aria-label="下移"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMovePlaylist(playlist.id, 1); }}
                    isDisabled={indexInGroup === groupSorted.length - 1}
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>

        {/* 排序模式下不展开内容，避免交互拥挤 */}
        {!isSortMode && expandedPlaylist === playlist.id && (
          <EuiDragDropContext onDragEnd={onDragEnd}>
            <EuiDroppable droppableId="DROPPABLE_AREA" spacing="none">
              {songs.map((song, index) => (
                <EuiDraggable spacing="none" key={song.resourceId} index={index} draggableId={song.name} paddingSize="none" hasBorder={true}>
                  {(provided, state) => (
                    <EuiPanel hasShadow={state.isDragging} paddingSize="none" color={currentSong.resourceId === song.resourceId ? "danger" : "none"} hasBorder={true}>
                      <EuiFlexGroup alignItems="center" gutterSize="s" style={{ flexWrap: 'nowrap' }}>
                        <EuiFlexItem grow={false} style={{ flexShrink: 0, flexBasis: '20px', minWidth: '20px', maxWidth: '20px' }}>
                          <EuiIcon type="grab" />
                        </EuiFlexItem>
                        <EuiFlexItem grow={true} style={{ flexShrink: 1, flexBasis: '100%' }}>
                          <EuiListGroupItem
                            className={`song-item ${currentSong && currentSong.resourceId === song.resourceId ? 'playing-song' : ''}`}
                            label={
                              <>
                                {TranslateLanguage(localStorage.userLanguage, song.resourceType)}:
                                {song.name.length > 9 ? TranslateLanguage(localStorage.userLanguage, song.name.substring(0, 9)) + '...' : TranslateLanguage(localStorage.userLanguage, song.name)}
                              </>
                            }
                            icon={
                              <>
                                <EuiIcon
                                  type="cross"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSong(song);
                                  }}
                                  style={{ marginLeft: '0px', marginRight: '10px', cursor: 'pointer' }}
                                />
                                <EuiIcon type={song.labels === "v" ? "play" : "document"} style={{ marginRight: '5px' }} />
                              </>
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.currentTarget.focus();
                              handleSongClick(song);
                            }}
                          />
                        </EuiFlexItem>
                      </EuiFlexGroup>
                    </EuiPanel>
                  )}
                </EuiDraggable>
              ))}
            </EuiDroppable>
          </EuiDragDropContext>
        )}
      </div>
    );
  };
  const pinnedPlaylists = (playlists || []).filter(p => !!p.pinned);
  const normalPlaylists = (playlists || []).filter(p => !p.pinned);


  return (
    <div className="playlistPageShell">
      <div className="appHeaderSticky">
        <EuiHeader className="no-shadow-header">
          <EuiHeaderSection>
            {/* 左侧：Logo / 搜索等 */}
          </EuiHeaderSection>
          <EuiHeaderSection side="right">
            <EuiHeaderSectionItem>
              <EuiButtonEmpty
                size="s"
                iconType="home"
                onClick={() => navigate('/')}
                color="text"
                iconSide="left"
                style={{
                  fontWeight: 500,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  height: '32px',
                  padding: '0 8px',
                }}
              >
                {TranslateLanguage(localStorage.userLanguage, "首頁")}
              </EuiButtonEmpty>
            </EuiHeaderSectionItem>
            <EuiHeaderSectionItem>
              <LanguageToggle />
            </EuiHeaderSectionItem>
            <EuiHeaderSectionItem>
              <TopRightMenu withLanguage="1" />
            </EuiHeaderSectionItem>
          </EuiHeaderSection>
        </EuiHeader>
      </div>

        <EuiFlexGroup
          className="responsive-flex-group favorite-page-flex"
          gutterSize="none"
          responsive={false}
        >
        {/* 左侧播放窗口 */}
        {/* <EuiFlexItem grow={isPanelVisible ? 2 : 3} style={{ position: 'sticky', top: 0, zIndex: 100 }} className={isPanelVisible ? "left_video_item" : "left_video_item_full"}> */}
        <EuiFlexItem 
          grow={false}
          className={
            isPanelVisible
              ? "left_video_item favorite-left-panel"
              : "left_video_item_full favorite-left-panel"
          }
          style={{ 
            zIndex: 100, 
            minWidth: 0,
            flexShrink: 0
          }} 
          >
          {/* <EuiPanel
            className="video-player-panel"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              padding: '5px',
            }}
          > */}
            <EuiPanel
              className="video-player-panel favorite-player-panel"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                width: '100%',
                height: '100%',
                minHeight: 0,
                padding: '5px',
                overflow: 'hidden'
              }}
            >
            <div className="favorite-panel-topbar">
              <EuiButtonIcon
                iconType={
                  isPanelVisible
                    ? 'arrowRight'
                    : 'arrowLeft'
                }
                onClick={togglePanel}
                aria-label={
                  isPanelVisible
                    ? '最大化播放面板'
                    : '显示收藏列表'
                }
              />
            </div>
            <div className={`favorite-left-scroll ${currentSong?.labels === "v" ? "favorite-left-scroll--video" : ""}`}>
            {showEmpty ? (
              <div className="empty-state">
                <EuiIcon type="iInCircle" size="xl" className="empty-icon" />
                <div className="empty-text">
                  {TranslateLanguage(localStorage.userLanguage, "沒有收藏單或收藏單内容爲空")}
                </div>
                <div className="empty-subtext">
                  {TranslateLanguage(localStorage.userLanguage, "請點擊「新建收藏單」或向當前收藏單添加內容")}
                </div>
              </div>
            ) : currentSong ? (
              currentSong.labels === "v" ? (
                <>
                  <h3 className="favorite-video-title">
                    {TranslateLanguage(localStorage.userLanguage, currentSong.name)}
                  </h3>
                  <div className="favorite-video-frame">
                    <YouTube
                      id="ytPlayer"
                      videoId={currentSong.resourceId}
                      className="favorite-video-player"
                      iframeClassName="favorite-video-iframe"
                      onReady={onReady}
                      opts={opts}
                      onError={onPlayerError}
                      onPlay={keepScreenAwakeForPlayback}
                      onPause={releaseScreenAwakeForPlayback}
                      onEnd={() => { releaseScreenAwakeForPlayback(); handleSongEnd(); }}
                    />
                  </div>
                </>
              ) : (
                <div style={panelStyle}>
                  <>
                    {!(["拾穗", "註解", "例證", "綱目"].includes(currentSong.resourceType)) && (
                      <>
                        {(currentSong.resourceType === '經文') && (
                          <div>
                            <div style={textVerseContainerStyle}>
                              <span>{TranslateLanguage(localStorage.userLanguage, verses)}</span>
                            </div>
                            <div style={{ ...textTitleStyle, textAlign: getTextAlignment(currentSong) }}>
                              <EuiText>
                                <span>{TranslateLanguage(localStorage.userLanguage, currentResource)}</span>
                              </EuiText>
                            </div>
                          </div>
                        )}
                        {!(currentSong.resourceType === '經文') && (
                          <div>
                            <div style={textTitleStyle}>
                              <span>{TranslateLanguage(localStorage.userLanguage, currentSong.name)}</span>
                            </div>
                            <div style={textContainerStyleWithTitle}>
                              <EuiText>
                                <span>{TranslateLanguage(localStorage.userLanguage, currentResource)}</span>
                              </EuiText>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {["拾穗", "註解", "例證", "綱目"].includes(currentSong.resourceType) && (
                      <div style={textContainerStyle}>
                        <EuiText>
                          <span dangerouslySetInnerHTML={{ __html: TranslateLanguage(localStorage.userLanguage, currentResource) }} />
                        </EuiText>
                      </div>
                    )}
                  </>
                  <div style={{ textAlign: 'right', marginTop: '10px' }}>
                    {currentSong.link && (
                      <EuiButton
                        size="s"
                        fill
                        onClick={() => window.open(currentSong.link, '_blank')}
                        style={{ backgroundColor: '#0077c2', color: '#fff' }}
                      >
                        {TranslateLanguage(localStorage.userLanguage, "鏈接來源")}
                      </EuiButton>
                    )}
                  </div>
                </div>
              )
            ) : (
              <p>{TranslateLanguage(localStorage.userLanguage, "請點擊收藏項目播放")}</p>
            )}

            {/* Controls */}
            {/* {currentSong && selectedPlaylist && (songs.length > 0) && ( */}
            <div className="favorite-player-footer">
              <div className="favorite-player-controls">
                {currentSong?.resourceId && songs.length > 0 && (
                  <button
                    type="button"
                    className="favorite-player-step favorite-player-step--previous"
                    onClick={handlePreviousSong}
                    aria-label={TranslateLanguage(localStorage.userLanguage, "上一項")}
                  >
                    <EuiIcon size="l" type="sortLeft" />
                  </button>
                )}

                <EuiButton
                  className="favorite-player-close"
                  size="s"
                  color="danger"
                  iconType="arrowLeft"
                  onClick={() => navigate('/')}
                >
                  {TranslateLanguage(localStorage.userLanguage, "關閉")}
                </EuiButton>

                {currentSong?.resourceId && songs.length > 0 && (
                  <button
                    type="button"
                    className="favorite-player-step favorite-player-step--next"
                    onClick={handleNextSong}
                    aria-label={TranslateLanguage(localStorage.userLanguage, "下一項")}
                  >
                    <EuiIcon size="l" type="sortRight" />
                  </button>
                )}
              </div>
            </div>
          </div>
          </EuiPanel>
        </EuiFlexItem>

        {/* 面板切换按钮 */}
        {/* <EuiFlexItem 
          grow={false} 
          className="center_toggle_button"
          style={{
            zIndex: 1,
            flex: '0 0 auto'
          }}
        >
          <EuiFlexGroup className="panel-toggle-button" direction="column" alignItems="center" justifyContent="center">
            <EuiFlexItem grow={false}>
              <EuiButtonIcon
                iconType={isPanelVisible ? 'arrowRight' : 'arrowLeft'}
                onClick={togglePanel}
                aria-label="Toggle Playlist Panel"
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem> */}

        {/* 右侧收藏单及收藏項目显示 */}
        {isPanelVisible && (
          <EuiFlexItem 
            // grow={1} 
            grow={false}
            // className="rigth_song_info" 
            className="rigth_song_info favorite-right-panel"
            style={{ 
              // height: '100vh', 
              // height: '100%',
              minWidth: 0,
              minHeight: 0,
              overflowY: 'auto' 
              }}
            >
            <EuiPanel
              className="playlist-list-panel playlist-page-list-panel"
              hasShadow={false}
              hasBorder={false}
              style={{
                width: '100%',
                height: 'auto',
                minHeight: '100%',
                maxHeight: 'none',
                textAlign: 'center',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible'
              }}
            >
              {/* 顶部Logo和标题部分 */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  marginBottom: '15px',
                  width: '100%'
                }}
                onClick={() => navigate('/')}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={logosmall} alt="Logo" style={{ width: '50px', height: '50px', marginBottom: '10px' }} />
                  <h2 style={{ margin: 10, textAlign: 'center' }}>
                    <span>{user ? `${TranslateLanguage(localStorage.userLanguage, user.username)}的收藏單` : '我的收藏單'}</span>
                    <span style={{ marginLeft: '8px', color: '#69707d', fontSize: '0.5em', fontWeight: 400, whiteSpace: 'nowrap' }}>
                      {playlists.length}{TranslateLanguage(localStorage.userLanguage, "個")}
                    </span>
                  </h2>
                </div>
              </div>

              {/* 收藏单和收藏項目列表部分 */}
              <div style={{ width: '100%' }}>
                <EuiListGroup flush>
                  {pinnedPlaylists.map(renderPlaylistRow)}

                  {pinnedPlaylists.length > 0 && normalPlaylists.length > 0 && (
                    <div style={{ margin: '8px 12px', borderTop: '2px solid #D3DAE6' }} />
                  )}

                  {normalPlaylists.map(renderPlaylistRow)}
                </EuiListGroup>

                <div style={{ width: '100%', padding: '5px', display: 'flex', justifyContent: 'space-evenly' }}>
                  {selectedPlaylist && (
                    <>
                      <EuiButtonEmpty iconType="advancedSettingsApp" size="s" onClick={handleEditClick} isDisabled={isSortMode}
              style={{ width: "100%", overflow: "hidden", pointerEvents: isSortMode ? "none" : "auto" }}>
                        {TranslateLanguage(localStorage.userLanguage, "收藏單詳情")}
                      </EuiButtonEmpty>
                      <EuiButtonEmpty iconType="share" size="s" onClick={handleSharePlaylistClick} isDisabled={isSortMode}
              style={{ width: "100%", overflow: "hidden", pointerEvents: isSortMode ? "none" : "auto" }}>
                        {TranslateLanguage(localStorage.userLanguage, "分享收藏單")}
                      </EuiButtonEmpty>
                    </>
                  )}
                </div>

                <div style={{ width: '100%', padding: '5px', display: 'flex', justifyContent: 'space-evenly' }}>
                  {selectedPlaylist && (
                    <>
                      <EuiButtonEmpty iconType="trash" size="s" onClick={handleDeletePlaylistClick} isDisabled={isSortMode}
              style={{ width: "100%", overflow: "hidden", pointerEvents: isSortMode ? "none" : "auto" }}>
                        {TranslateLanguage(localStorage.userLanguage, "刪除收藏單")}
                      </EuiButtonEmpty>
                    </>
                  )}

                  <EuiButtonEmpty iconType="listAdd" size="s" onClick={openCreatePlaylistModal} isDisabled={isSortMode}
              style={{ width: "100%", overflow: "hidden", pointerEvents: isSortMode ? "none" : "auto" }}>
                    {TranslateLanguage(localStorage.userLanguage, "新建收藏單")}
                  </EuiButtonEmpty>
                </div>

                <div style={{ width: '100%', padding: '5px', display: 'flex', justifyContent: 'space-evenly' }}>

                  {/* ✅ 新增：收藏单排序按钮（同歌单排序方案） */}
                  {!isSortMode ? (
                    <EuiButtonEmpty iconType="sortable" size="s" onClick={handleEnterSortMode}>
                      {TranslateLanguage(localStorage.userLanguage, "收藏單排序")}
                    </EuiButtonEmpty>
                  ) : (
                    <>
                      <EuiButtonEmpty iconType="check" size="s" onClick={handleSavePlaylistOrder} isDisabled={isSavingPlaylistOrder}>
                        {TranslateLanguage(localStorage.userLanguage, "完成")}
                      </EuiButtonEmpty>
                      <EuiButtonEmpty iconType="cross" size="s" color="danger" onClick={handleCancelSortMode} isDisabled={isSavingPlaylistOrder}>
                        {TranslateLanguage(localStorage.userLanguage, "取消")}
                      </EuiButtonEmpty>
                    </>
                  )}
                </div>
              </div>
            </EuiPanel>
          </EuiFlexItem>
        )}

        {/* 如果生成了分享链接，显示链接和复制按钮 */}
        {shareLink && (
          <EuiModal
            onClose={() => setShareLink(false)}
            style={{
              maxWidth: '90%',
              height: '90%',
              minHeight: '50vh',
              margin: 'auto'
            }}
          >
            <EuiModalHeader>
              <h2 style={{ textAlign: 'center', width: '100%' }}>
                {TranslateLanguage(localStorage.userLanguage, "分享鏈接")}
              </h2>
            </EuiModalHeader>

            <EuiModalBody style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <div style={{ overflowX: 'auto', wordBreak: 'break-all', maxWidth: '100%' }}>
                <p style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{shareLink}</p>
              </div>
            </EuiModalBody>

            <EuiModalFooter style={{ display: 'flex' }}>
              <EuiButton fill onClick={() => copyShareLink(shareLink)}>
                {TranslateLanguage(localStorage.userLanguage, "複製鏈接")}
              </EuiButton>
              <EuiButton onClick={() => setShareLink(false)} color="danger">
                {TranslateLanguage(localStorage.userLanguage, "關閉")}
              </EuiButton>
            </EuiModalFooter>
          </EuiModal>
        )}

        {/* 编辑收藏单模态窗口 */}
        {isEditModalVisible && (
          <EuiModal 
            onClose={() => setIsEditModalVisible(false)}
            style={{
              maxWidth: '90%',
              height: '90%',
              minHeight: '50vh',
              margin: 'auto'
            }}
          >
            <EuiModalHeader>
              <h2>{TranslateLanguage(localStorage.userLanguage, "收藏單詳情")}</h2>
            </EuiModalHeader>
            <EuiModalBody>
              <EuiFieldText
                placeholder={TranslateLanguage(localStorage.userLanguage, "收藏單名稱")}
                value={TranslateLanguage(localStorage.userLanguage, newPlaylistName)}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                required
                aria-label="收藏單名稱"
                isInvalid={!newPlaylistName.trim()}
              />
              <EuiSelect
                options={[
                  { value: 'public', text: TranslateLanguage(localStorage.userLanguage, "公開") },
                  { value: 'private', text: TranslateLanguage(localStorage.userLanguage, "私有") },
                  { value: 'shareable', text: TranslateLanguage(localStorage.userLanguage, "可分享但不加入搜索列表") }
                ]}
                value={newPlaylistVisibility}
                onChange={(e) => setNewPlaylistVisibility(e.target.value)}
              />
              <EuiTextArea
                placeholder={TranslateLanguage(localStorage.userLanguage, "收藏單描述")}
                value={newPlaylistDescription}
                onChange={(e) => setNewPlaylistDescription(e.target.value)}
              />
              <EuiFieldText
                placeholder={TranslateLanguage(localStorage.userLanguage, "標籤(用逗號分隔)")}
                value={newPlaylistTags}
                onChange={(e) => setNewPlaylistTags(e.target.value)}
              />
              <EuiFieldText
                placeholder={TranslateLanguage(localStorage.userLanguage, "分類")}
                value={newPlaylistCategory}
                onChange={(e) => setNewPlaylistCategory(e.target.value)}
              />
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButton
                onClick={() => {
                  if (newPlaylistName.trim()) {
                    handleSaveChanges();
                  } else {
                    showAlert(TranslateLanguage(localStorage.userLanguage, "收藏單名稱為必填項"));
                  }
                }}
                fill
              >
                {TranslateLanguage(localStorage.userLanguage, "保存更改")}
              </EuiButton>
              <EuiButton onClick={() => setIsEditModalVisible(false)} color="danger">
                {TranslateLanguage(localStorage.userLanguage, "取消")}
              </EuiButton>
            </EuiModalFooter>
          </EuiModal>
        )}

        {/* 新建收藏单模态窗口 */}
        {showCreateModal && (
          <EuiModal 
            onClose={closeCreatePlaylistModal}
            style={{
              maxWidth: '90%',
              height: '90%',
              minHeight: '50vh',
              margin: 'auto'
            }}
          >
            <EuiModalHeader>
              <h2>{TranslateLanguage(localStorage.userLanguage, "創建新的收藏單")}</h2>
            </EuiModalHeader>
            <EuiModalBody>
              <EuiFieldText
                placeholder={TranslateLanguage(localStorage.userLanguage, "收藏單名稱")}
                value={TranslateLanguage(localStorage.userLanguage, newPlaylistName)}
                onChange={e => setNewPlaylistName(e.target.value)}
                required
                aria-label="收藏單名稱"
                isInvalid={!newPlaylistName.trim()}
              />
              <EuiSelect
                options={[
                  { value: 'public', text: TranslateLanguage(localStorage.userLanguage, "公開") },
                  { value: 'private', text: TranslateLanguage(localStorage.userLanguage, "私有") },
                  { value: 'shareable', text: TranslateLanguage(localStorage.userLanguage, "可分享但不加入搜索列表") }
                ]}
                value={newPlaylistVisibility}
                onChange={e => setNewPlaylistVisibility(e.target.value)}
              />
              <EuiTextArea
                placeholder={TranslateLanguage(localStorage.userLanguage, "收藏單描述")}
                value={newPlaylistDescription}
                onChange={e => setNewPlaylistDescription(e.target.value)}
              />
              <EuiFieldText
                placeholder={TranslateLanguage(localStorage.userLanguage, "標籤(用逗號分隔)")}
                value={newPlaylistTags}
                onChange={e => setNewPlaylistTags(e.target.value)}
              />
              <EuiFieldText
                placeholder={TranslateLanguage(localStorage.userLanguage, "分類")}
                value={newPlaylistCategory}
                onChange={e => setNewPlaylistCategory(e.target.value)}
              />
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButton
                onClick={() => {
                  if (newPlaylistName.trim()) {
                    handleCreatePlaylist();
                  } else {
                    showAlert(TranslateLanguage(localStorage.userLanguage, "收藏單名稱為必填項"));
                  }
                }}
                fill
              >
                {TranslateLanguage(localStorage.userLanguage, "創建")}
              </EuiButton>
              <EuiButton onClick={closeCreatePlaylistModal} color="danger">
                {TranslateLanguage(localStorage.userLanguage, "取消")}
              </EuiButton>
            </EuiModalFooter>
          </EuiModal>
        )}
      </EuiFlexGroup>
    </div>
  );
}

export default FavoritePageWithPlayer;
